// src/admin/auth/AuthProvider.tsx
import { useEffect, useState, useCallback, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { AuthContext, type AdminRow, type AuthContextValue } from './AuthContext';

type Aal = 'aal1' | 'aal2' | null;

async function loadAdminRow(userId: string): Promise<AdminRow | null> {
  const { data } = await supabase
    .from('admin_users')
    .select('*')
    .eq('user_id', userId)
    .eq('is_active', true)
    .maybeSingle();
  return (data as AdminRow | null) ?? null;
}

async function loadAal(): Promise<Aal> {
  const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (!data) return null;
  return (data.currentLevel as Aal) ?? null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [admin, setAdmin] = useState<AdminRow | null>(null);
  const [aal, setAal] = useState<Aal>(null);
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');

  const refresh = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    setSession(data.session);
    setUser(data.session?.user ?? null);
    if (data.session?.user) {
      const [row, level] = await Promise.all([
        loadAdminRow(data.session.user.id),
        loadAal(),
      ]);
      setAdmin(row);
      setAal(level);
      setStatus('signed_in');
    } else {
      setAdmin(null);
      setAal(null);
      setStatus('signed_out');
    }
  }, []);

  useEffect(() => {
    void refresh();
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      void refresh();
    });
    return () => sub.subscription.unsubscribe();
  }, [refresh]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setAdmin(null);
    setAal(null);
    setStatus('signed_out');
  }, []);

  const value: AuthContextValue = { session, user, admin, aal, status, signOut, refresh };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
